// Import the GeoJSON data directly (renamed to .json for ES module compatibility)
import worldCountriesData from './World_Countries.json'

/**
 * Load the world countries GeoJSON data
 * @returns {Promise<Object>} The GeoJSON data for world countries
 */
export const loadCountriesGeoJSON = async () => {
  try {
    // Return the imported data directly
    return worldCountriesData
  } catch (error) {
    console.error('Error loading countries GeoJSON:', error)
    throw error
  }
}

/**
 * Get country information from GeoJSON by country code
 * @param {Object} geoJsonData - The loaded GeoJSON data
 * @param {string} countryCode - ISO country code (e.g., 'US', 'DE', 'FR')
 * @returns {Object|null} Country feature object or null if not found
 */
export const getCountryByCode = (geoJsonData, countryCode) => {
  if (!geoJsonData || !geoJsonData.features) {
    return null
  }

  return geoJsonData.features.find(
    feature =>
      feature.properties.ISO_A2 === countryCode ||
      feature.properties.ISO_A3 === countryCode ||
      feature.properties.ADM0_A3 === countryCode,
  )
}

/**
 * Get country center coordinates from GeoJSON
 * @param {Object} geoJsonData - The loaded GeoJSON data
 * @param {string} countryCode - ISO country code
 * @returns {Object|null} Object with lat, lon properties or null if not found
 */
export const getCountryCenter = (geoJsonData, countryCode) => {
  const country = getCountryByCode(geoJsonData, countryCode)
  if (!country || !country.geometry) {
    return null
  }

  // For polygons, calculate centroid
  if (country.geometry.type === 'Polygon') {
    const coordinates = country.geometry.coordinates[0]
    let lat = 0
    let lon = 0
    coordinates.forEach(coord => {
      lon += coord[0]
      lat += coord[1]
    })
    return {
      lat: lat / coordinates.length,
      lon: lon / coordinates.length,
    }
  }

  // For MultiPolygon, use first polygon centroid
  if (country.geometry.type === 'MultiPolygon') {
    const coordinates = country.geometry.coordinates[0][0]
    let lat = 0
    let lon = 0
    coordinates.forEach(coord => {
      lon += coord[0]
      lat += coord[1]
    })
    return {
      lat: lat / coordinates.length,
      lon: lon / coordinates.length,
    }
  }

  return null
}

/**
 * Get all available countries from GeoJSON
 * @param {Object} geoJsonData - The loaded GeoJSON data
 * @returns {Array} Array of country objects with code, name, and center
 */
export const getAllCountries = geoJsonData => {
  if (!geoJsonData || !geoJsonData.features) {
    return []
  }

  return geoJsonData.features
    .map(feature => ({
      code: feature.properties.ISO_A2 || feature.properties.ISO_A3,
      name: feature.properties.NAME || feature.properties.NAME_EN,
      center: getCountryCenter(
        geoJsonData,
        feature.properties.ISO_A2 || feature.properties.ISO_A3,
      ),
    }))
    .filter(country => country.code && country.center)
}

const geojsonUtils = {
  loadCountriesGeoJSON,
  getCountryByCode,
  getCountryCenter,
  getAllCountries,
}

export default geojsonUtils
